export const BRANCH_TRANSFER_MARGIN_PERCENT = 55;

// 1. Column Order for Stock Transfer In
export const stockTransferInDefaultColumnsOrder = [
  "Slno",
  "Code",
  "Description",
  "Locn",
  "Unit",
  "Qty",
  "UnitCost",
  "Margin",
  "BilledPrice",
  "BilledAmount",
  "Amount",
  "Fraction",
  "AvlQty",
  "ReqDep",
  "ReqNo",
];

// 2. Mapping: Header → Field Name
export const stockTransferInColumnFieldMap = {
  Slno: "sino",
  Code: "code",
  Description: "description",
  Locn: "location",
  Unit: "unit",
  Qty: "qty",
  Margin: "margin",
  BilledPrice: "billedPrice",
  BilledAmount: "billedAmount",
  UnitCost: "unitCost",
  Amount: "amount",
  Fraction: "fraction",
  AvlQty: "avlQty",
  ReqDep: "reqDep",
  ReqNo: "reqNo",
};

// 3. Base Column Generator
export const getStockTransferInBaseColumn = (
  field,
  widthMap,
  renderers,
  locationOptions,
  unitOptions,
  documentData,
) => {
  let headerName =
    Object.entries(stockTransferInColumnFieldMap).find(
      ([, value]) => value === field,
    )?.[0] || field;

  if (field === "margin") {
    const marginPercent =
      documentData?.marginPercent !== undefined
        ? documentData.marginPercent
        : BRANCH_TRANSFER_MARGIN_PERCENT;
    headerName = `Margin (${marginPercent}%)`;
  }

  const baseConfig = {
    field,
    headerName,
    width: widthMap?.[field] || 100,
    sortable: false,
    filterable: false,
    disableColumnMenu: false,
  };

  // Editable Numeric Fields
  const numericEditableFields = ["qty", "unitCost", "margin", ""];
  if (numericEditableFields.includes(field)) {
    return {
      ...baseConfig,
      renderCell: (params) =>
        renderers.renderEditableCell(params, field, "number"),
    };
  }

  // Calculated / Read-only Numeric Fields
  const numericReadOnlyFields = [
    "fraction",
    "avlQty",
    "amount",
    "billedPrice",
    "billedAmount",
  ];
  if (numericReadOnlyFields.includes(field)) {
    return {
      ...baseConfig,
      type: "number",
      renderCell: (params) =>
        typeof params.value === "number"
          ? params.value.toFixed(2)
          : params.value?.toLocaleString() || 0,
    };
  }

  // Editable Text fields
  if (["code", "description", "reqDep", "reqNo"].includes(field)) {
    return {
      ...baseConfig,
      renderCell: (params) => renderers.renderEditableCell(params, field),
    };
  }

  // Dropdowns
  if (field === "location") {
    return {
      ...baseConfig,
      renderCell: (params) =>
        renderers.renderSelectCell(params, "location", locationOptions),
    };
  }

  if (field === "unit") {
    return {
      ...baseConfig,
      renderCell: (params) =>
        renderers.renderSelectCell(params, "unit", unitOptions),
    };
  }

  return baseConfig;
};

// margin       = unitCost × marginPercent% (per-unit margin amount)
// billedPrice  = unitCost + margin         (per-unit billed price)
// billedAmount = qty × billedPrice         (total line billed amount)
// amount       = unitCost × qty            (total line cost amount)
export const calculateStockTransferInRow = (item, documentData = {}) => {
  const qty = Number(item.qty) || 0;
  const unitCost = Number(item.unitCost) || 0;
  const amount = unitCost * qty;

  const isBranchTransfer = documentData?.documentType === "BRANCHTRANSFER";

  if (isBranchTransfer) {
    const marginPercent = Number(item.margin) ||
      (documentData?.marginPercent !== undefined
        ? Number(documentData.marginPercent)
        : BRANCH_TRANSFER_MARGIN_PERCENT);

    const marginAmount = (unitCost * marginPercent) / 100;
    const billedPrice = unitCost + marginAmount;
    const billedAmount = qty * billedPrice;

    return {
      ...item,
      qty,
      unitCost,
      amount,
      margin: marginPercent,
      billedPrice,
      billedAmount,
      total: billedAmount,
    };
  }

  return {
    ...item,
    qty,
    unitCost,
    amount,
    margin: 0,
    billedPrice: unitCost,
    billedAmount: amount,
    total: amount,
  };
};

// 5. Required Fields for Validation
export const stockTransferInRequiredFields = [
  "code",
  "location",
  "unit",
  "qty",
  "unitCost",
];

// 6. New Item Template
export const newStockTransferInItem = (id, sino, defaultLocation) => ({
  id,
  sino,
  code: "",
  description: "",
  location: defaultLocation,
  unit: "",
  qty: 0,
  unitCost: 0,
  amount: 0,
  margin: BRANCH_TRANSFER_MARGIN_PERCENT,
  billedPrice: 0,
  billedAmount: 0,
  fraction: 1,
  avlQty: 0,
  reqDep: "",
  reqNo: "",
  isIncluded: true,
});

// 7. Column Width Percent Defaults (Adjusted for more columns)
export const stockTransferInColumnPercents = {
  sino: 5,
  code: 10,
  description: 15,
  location: 10,
  unit: 8,
  qty: 8,
  margin: 8,
  billedPrice: 8,
  billedAmount: 8,
  unitCost: 8,
  amount: 8,
  fraction: 6,
  avlQty: 6,
  reqDep: 8,
  reqNo: 8,
};

const screenCode = "STOCKTRANSFERIN";

export const stockTransferInConfig = {
  defaultColumnsOrder: stockTransferInDefaultColumnsOrder,
  columnFieldMap: stockTransferInColumnFieldMap,
  getBaseColumn: getStockTransferInBaseColumn,
  calculateRowValues: calculateStockTransferInRow,
  requiredFields: stockTransferInRequiredFields,
  screenCode,
  newItemTemplate: newStockTransferInItem,
  defaultColumnPercents: stockTransferInColumnPercents,
};

export default stockTransferInConfig;
